import React from 'react';

import { TooltipPortal } from '~components';

// Short enough that pausing on a header feels answered rather than laggy, long enough that
// sweeping the pointer across the header row does not fire a caption for every column it crosses.
// The dotted underline responds on hover with no delay at all, so this only gates the tooltip
// itself - the affordance still confirms instantly that the header has something to say.
const HOVER_DELAY_MS = 100;

interface ComponentProps {
  // Unique per rendered header cell; two tables on one page must not collide on it.
  targetId: string;
  label: React.ReactNode;
  caption: string;
  // The sort affordance for a sortable column, folded into this tooltip rather than left on the
  // header's native `title`. A `title` on the same element as a custom tooltip means one hover
  // opens both, so a captioned column surfaces the sort hint here instead.
  sortHint?: string;
}

/**
 * A column header that explains itself on hover.
 *
 * Table headers are short to the point of being ambiguous - on the Support at Home associations
 * list, "SAH Service" and "Associated Services" are two different things whose names do not say
 * which is the government's and which is the provider's. A one-line caption on hover resolves
 * that without spending column width on it.
 *
 * The caption is signalled by a dotted underline on the label rather than by an icon: it is the
 * long-standing `<abbr>` convention for "this term has an expansion", it is painted rather than
 * laid out so it costs no header real estate, and it does not compete with the sort glyph beside
 * it the way a second icon would. A header with nothing extra to say carries no underline, so the
 * marker's presence is itself the information.
 */
const ColumnHeaderTooltip: React.FC<ComponentProps> = ({ targetId, label, caption, sortHint }) => (
  <>
    <span id={targetId} className="has-caption">
      {label}
    </span>
    <TooltipPortal
      targetId={targetId}
      placement="top"
      showDelay={HOVER_DELAY_MS}
      content={
        <>
          {caption}
          {sortHint && <div className="caption-sort-hint">{sortHint}</div>}
        </>
      }
    />
  </>
);

export default ColumnHeaderTooltip;
